import type { FeatureApiResponse } from "@growthbook/growthbook";

import type { ToMaybeRefs } from "@solvari/utils";

import { awaitTimeout } from "@solvari/utils";

import type { BrowserAttributes } from "@/client/browserAttributes.ts";
import type { ClientFeature } from "@/client/features.ts";
import type { SiteAttributes } from "@/core/attributes.ts";
import type { AbTestingRouter } from "@/features/splitUrl/client.ts";

import { runFeatures } from "@/client/features.ts";
import { createAbTestingClient } from "@/client/instance.ts";
import { loadedTimeout, markAbTestingLoaded } from "@/client/loaded.ts";
import { getSentryAbTesting } from "@/client/sentry.ts";
import { linkMapFeature } from "@/features/linkMap/client.ts";
import { splitUrlFeature } from "@/features/splitUrl/client.ts";

type AbTestingOptions = {
  browserAttributes?: BrowserAttributes;
  features?: readonly (() => ClientFeature)[];
  // The render slice the server rendered with → setPayload for hydration parity, then the
  // CDN payload is fetched and merged under it. Absent → network init, today's path.
  payload?: FeatureApiResponse;
  router?: AbTestingRouter;
};

function defaultFeatures(
  router?: AbTestingRouter,
): readonly (() => ClientFeature)[] {
  return [() => splitUrlFeature({ router }), () => linkMapFeature()];
}

async function runAbTesting(
  clientId: string,
  siteAttributes: ToMaybeRefs<SiteAttributes>,
  options: AbTestingOptions,
) {
  try {
    const instance = await createAbTestingClient({
      browserAttributes: options.browserAttributes,
      clientId,
      payload: options.payload,
      siteAttributes,
    });

    await runFeatures(
      instance,
      options.features ?? defaultFeatures(options.router),
      {
        // A rejected router isReady() must not sink the whole feature run.
        navigationReady:
          options.router?.isReady().catch(() => undefined) ?? Promise.resolve(),
      },
    );
  } catch (error) {
    getSentryAbTesting().captureException(error);
  }
}

// Triggers anti-flicker reveal even for gated visitors — page stays hidden otherwise.
async function initAbTesting(
  clientId: string | null,
  siteAttributes: ToMaybeRefs<SiteAttributes>,
  options: AbTestingOptions = {},
) {
  try {
    // No client id → the visitor is in no test at all; nothing will ever load.
    if (clientId === null) {
      markAbTestingLoaded();
      return;
    }

    await Promise.race([
      runAbTesting(clientId, siteAttributes, options),
      awaitTimeout(loadedTimeout),
    ]);
  } finally {
    // Contract: `libs/components/src/other/abTestingAntiFlicker.blade.php` defines
    // `abTestingRender` and its own 1 s fallback — absent hook → page was never hidden.
    globalThis.abTestingRender?.();
  }
}

export type { AbTestingOptions };
export { initAbTesting };
